import { observer } from 'mobx-react';
import { InfoCircleOutlined } from '@ant-design/icons';
import { useCallback, useEffect, useState } from 'react';
import { ExportButton } from '~components/action-buttons';
import { PageTopBar } from '~components/page-top-bar';
import { TopTabs } from '~components/top-tabs';
import { AnalyticsSearchOptions } from '~components/analytics-search-options';
import {
    useAnalyticsStore,
    useApplicationSelectionStore,
    useAuthStore,
} from '~contexts/mobx';
import { useNavigation } from '~utils/use-navigation';
import { AskAIButton } from '~analytics-components/ask-ai-button';
import { SectionHeader } from '~analytics-components/section-header';
import { KpiLoadProgressBar } from '~analytics-components/kpi-load-progress-bar';
import { KpiCardFromSpec } from '~containers/analytics/kpis/kpi-card-from-spec';
import { OverviewToolbar } from '~containers/analytics/overview-page/overview-toolbar';
import { usePageFilter } from '~containers/analytics/overview-page/use-page-filter';
import type { PageFilterQuery } from '~containers/analytics/overview-page/use-page-filter';
import type { KpiCardSpec } from '~containers/analytics/kpis/card-spec';
import { lastSelectableDay } from '~containers/analytics/kpis/periods';
import { ROAD_TO_REWARDS_CAMPAIGN_ID } from '~containers/analytics/kpis/sources/road-to-rewards-campaign';
import { useSearchParams } from '~utils/use-search-params';
import {
    CAMPAIGN_DETAILS_CARDS,
    ENROLLMENT_FUNNEL_CARDS,
    ENROLLMENT_BY_BU_CARDS,
    ENROLLMENT_OVER_TIME_CARDS,
    MEMBER_DETAILS_CARDS,
    MEMBERS_VISITS_CARDS,
    CAMPAIGN_VISITS_LEDGER_CARDS,
    CAMPAIGN_VISITS_LEDGER_SCOPE_MARKER,
    MEMBERS_VISITS_PER_MILESTONE_CARDS,
    MEMBERS_CONTACTABILITY_CARDS,
    COUPONS_OVERVIEW_CARDS,
} from './cards';
import { useCampaignWindow } from './use-campaign-window';
import { useCampaignCouponStock } from './use-campaign-coupon-stock';
import { useCampaignTierLadder } from './use-campaign-tier-ladder';
import { useCampaignPromotionActivity } from './use-campaign-promotion-activity';
import { CampaignCouponStockCards } from './coupon-stock-cards';
import { RewardsInventoryTable } from './rewards-inventory-table';
import { CustomerGroupBlock } from './customer-group-block';
import { GlossaryTable } from './glossary-table';
import style from './style.module.scss';

/** The page's own query params, `usePageFilter`'s plus the tab: one
 * `useSearchParams` instance, so a write to either can never merge onto
 * the other's stale mirror of the same URL and clobber it. */
type RoadToRewardsSearchQuery = PageFilterQuery & { tab?: string };

const DEFAULT_TAB = 'enrollment';

/**
 * Road to Rewards analytics dashboard: the Enrollment, Members and visits,
 * and Milestone rewards tabs, plus the campaign window every card on any of
 * them is framed by, and a Glossary tab defining each KPI.
 */
export const RoadToRewardsAnalyticsPage = observer(() => {
    const authStore = useAuthStore();
    const analyticsStore = useAnalyticsStore();
    const navigation = useNavigation();
    const applicationSelectionStore = useApplicationSelectionStore();
    const search = useSearchParams<RoadToRewardsSearchQuery>();
    const { campaign, context } = useCampaignWindow();
    const { filter, setPeriod, setPreset, setRegion } = usePageFilter({
        search,
        region: {
            appTarget: applicationSelectionStore.appTarget,
            defaultProvincesCsv:
                applicationSelectionStore.getProvincesFromSelectedApplication,
        },
        defaultPeriod: 'campaign',
        campaign: campaign && {
            window: campaign,
            campaignId: ROAD_TO_REWARDS_CAMPAIGN_ID,
        },
    });
    const couponStock = useCampaignCouponStock();
    const tierLadder = useCampaignTierLadder();
    const promotionActivity = useCampaignPromotionActivity(
        context,
        filter.provinces
    );

    const { setParam: setSearchParam } = search;
    const [activeTab, setActiveTab] = useState(
        () => search.params.tab ?? DEFAULT_TAB
    );

    // Keeps the tab in step with browser back/forward, which changes the URL
    // without going through handleTabChange. Back onto a URL that carries no
    // `tab` is a move to the default one, not an absence of instruction.
    useEffect(() => {
        const urlTab = search.params.tab ?? DEFAULT_TAB;
        if (urlTab !== activeTab) {
            setActiveTab(urlTab);
        }
    }, [activeTab, search.params.tab]);

    const handleTabChange = useCallback(
        (key: string) => {
            setActiveTab(key);
            setSearchParam('tab', key);
        },
        [setSearchParam]
    );

    // Management access alone, where the other analytics pages also admit a
    // customer service agent: this campaign's nav entry sits in the Clubs group,
    // which is management-only, so a looser guard here would outrun it.
    useEffect(() => {
        if (!authStore.hasManagementAccess) {
            navigation.goToHome();
        }
    }, [authStore.hasManagementAccess, navigation]);

    const renderCard = (spec: KpiCardSpec) => (
        <KpiCardFromSpec
            key={spec.title}
            spec={spec}
            pageRange={filter.range}
            pageRegion={filter.provinces}
            descriptionOnlyInfo
            compareEnabled={filter.compare}
            waveKey={filter.waveKey}
            campaign={context}
        />
    );

    return (
        <>
            <PageTopBar title="Road to rewards - Analytics">
                <ExportButton
                    onClick={() =>
                        analyticsStore.downloadWeeklyReport(
                            'road-to-rewards-2026',
                            'Road to Rewards Weekly Campaign Report'
                        )
                    }
                    loading={analyticsStore.isDownloadingReport(
                        'road-to-rewards-2026'
                    )}
                >
                    Export report
                </ExportButton>
                <AskAIButton />
            </PageTopBar>
            <TopTabs
                variant="page-header"
                // The Glossary defines the KPIs; no period or region applies.
                belowTabBar={
                    activeTab === 'glossary' ? undefined : (
                        <div className={style.filters_block}>
                            <OverviewToolbar
                                filters={
                                    <AnalyticsSearchOptions
                                        startDate={filter.range.start}
                                        endDate={filter.range.end}
                                        updateParams={setPeriod}
                                        lastSelectableDay={lastSelectableDay()}
                                        periodPresets={{
                                            active: filter.preset,
                                            onSelect: setPreset,
                                            campaign,
                                            hasToDateTotals: true,
                                        }}
                                        regionFilter={{
                                            value: filter.provinces,
                                            disabled: filter.regionLocked,
                                            onChange: setRegion,
                                        }}
                                    />
                                }
                            />

                            <KpiLoadProgressBar />
                        </div>
                    )
                }
                activeKey={activeTab}
                onChange={handleTabChange}
                tabsArray={[
                    {
                        name: 'Enrollment',
                        key: 'enrollment',
                        children: (
                            <div className={style.dashboard}>
                                <section className={style.section}>
                                    <SectionHeader title="Campaign details" />
                                    <div className={style.grid}>
                                        {CAMPAIGN_DETAILS_CARDS.map(renderCard)}
                                    </div>
                                </section>

                                <section className={style.section}>
                                    <div className={style.dashboard_grid}>
                                        {ENROLLMENT_FUNNEL_CARDS.map(
                                            renderCard
                                        )}
                                    </div>
                                </section>

                                <section className={style.section}>
                                    <div className={style.dashboard_grid}>
                                        {ENROLLMENT_BY_BU_CARDS.map(renderCard)}
                                    </div>
                                </section>

                                <section className={style.section}>
                                    <div className={style.dashboard_grid}>
                                        {ENROLLMENT_OVER_TIME_CARDS.map(
                                            renderCard
                                        )}
                                    </div>
                                </section>
                            </div>
                        ),
                    },
                    {
                        name: 'Members and visits',
                        key: 'members-and-visits',
                        children: (
                            <div className={style.dashboard}>
                                <section className={style.section}>
                                    <SectionHeader title="Member details" />
                                    <div className={style.grid}>
                                        {MEMBER_DETAILS_CARDS.map(renderCard)}
                                    </div>
                                </section>

                                <section className={style.section}>
                                    <SectionHeader title="Members visits" />
                                    <div className={style.grid}>
                                        {MEMBERS_VISITS_CARDS.map(renderCard)}
                                    </div>
                                </section>

                                <section className={style.section}>
                                    <SectionHeader
                                        title="Visit ledger"
                                        subtitle={CAMPAIGN_VISITS_LEDGER_SCOPE_MARKER.toLowerCase()}
                                    />
                                    <div className={style.grid}>
                                        {CAMPAIGN_VISITS_LEDGER_CARDS.map(
                                            renderCard
                                        )}
                                    </div>
                                </section>

                                <section className={style.section}>
                                    <SectionHeader title="Members visits per milestone" />
                                    <div className={style.dashboard_grid}>
                                        {MEMBERS_VISITS_PER_MILESTONE_CARDS.map(
                                            renderCard
                                        )}
                                    </div>
                                </section>

                                <section className={style.section}>
                                    <SectionHeader title="Members Contactability" />
                                    <div className={style.dashboard_grid}>
                                        {MEMBERS_CONTACTABILITY_CARDS.map(
                                            renderCard
                                        )}
                                    </div>
                                </section>
                            </div>
                        ),
                    },
                    {
                        name: 'Milestone rewards',
                        key: 'coupons',
                        children: (
                            <div className={style.dashboard}>
                                <section className={style.section}>
                                    <SectionHeader title="Coupons overview" />
                                    <div className={style.grid}>
                                        {COUPONS_OVERVIEW_CARDS.slice(0, 6).map(
                                            renderCard
                                        )}
                                        <CampaignCouponStockCards
                                            state={couponStock}
                                            provinces={filter.provinces}
                                        />
                                        {COUPONS_OVERVIEW_CARDS.slice(6).map(
                                            renderCard
                                        )}
                                    </div>
                                </section>

                                <section className={style.section}>
                                    <SectionHeader
                                        title="Coupons inventory for active members"
                                        subtitle="Activity through yesterday. Stock is live."
                                    />
                                    <RewardsInventoryTable
                                        tierLadder={tierLadder}
                                        couponStock={couponStock}
                                        activity={promotionActivity}
                                        provinces={filter.provinces}
                                    />
                                </section>

                                <CustomerGroupBlock
                                    pageRange={filter.range}
                                    pageRegion={filter.provinces}
                                    compareEnabled={filter.compare}
                                    waveKey={filter.waveKey}
                                    campaign={context}
                                    tierLadder={tierLadder}
                                />
                            </div>
                        ),
                    },
                    {
                        name: 'Glossary',
                        key: 'glossary',
                        alignEnd: true,
                        icon: <InfoCircleOutlined />,
                        children: <GlossaryTable />,
                    },
                ]}
            />
        </>
    );
});
